import React from "react";
import { Info } from "lucide-react";
import CircularProgress from "../../common/CircularProgress";
import InfoTooltip from "../../common/InfoTooltip";
import PatientLimitField from "./PatientLimitField";
import { normalizeForCompare } from "../../../utils/normalizeForCompare";
import { useIsPwaApp } from "../../../pwa/PwaModeContext";

/** Estado de la validación IA del certificado para este servicio (Pareja/Familia/OV). */
type CertUIStatus = 'validating' | 'needs_additional_document' | 'rejected_escalated';
type TagItem = { name: string; active: boolean; certStatus?: CertUIStatus };

interface ProfessionalData {
  services: TagItem[];
  themes: TagItem[];
  approaches: TagItem[];
}

/** Nombres (normalizados) recién dados de alta/baja, sin guardar todavía, en una categoría de chips. */
interface PendingChangeSet {
  added: Set<string>;
  removed: Set<string>;
}

interface ProfileAttentionTabProps {
  professionalData: ProfessionalData;
  toggleTag: (
    category: "services" | "themes" | "approaches",
    index: number,
    e: React.MouseEvent,
  ) => void;
  servicesLoading: boolean;
  loadingProfile: boolean;
  preferencesCatalogLoading: boolean;
  /** Filtros habilitados para psi-platform además de Temáticas/Enfoques —
   *  clickeables solo si `writable` (ver comentario en PsychologistProfile.tsx). */
  otherPreferenceGroups: Array<{
    filterKey: string;
    label: string;
    writable: boolean;
    items: Array<{ name: string; active: boolean }>;
  }>;
  toggleOtherPreference: (filterKey: string, index: number, e: React.MouseEvent) => void;
  pendingOtherChanges: Record<string, PendingChangeSet>;
  /** Título de Temáticas/Enfoques — también viene del catálogo dinámico, no hardcodeado. */
  dedicatedSectionLabels: { themes: string; approaches: string };
  onSave: () => void;
  canSave: boolean;
  pendingAttentionChanges: {
    services: PendingChangeSet;
    themes: PendingChangeSet;
    approaches: PendingChangeSet;
  };
  patientLimit: {
    processLimit: number | null;
    draft: number | null;
    currentAssignedProcesses: number | null;
    onChange: (next: number) => void;
    onApplied: (next: number) => void;
    isModified: boolean;
    psychologistId: string | null;
  };
  hidePatientLimit?: boolean;
  /** Relanza el recorrido virtual de esta pestaña — botón sutil junto al título. */
  onGuideReplay?: () => void;
}

/** Estilo de "cambio pendiente de guardar": anillo + relleno suave del mismo color, con texto
 *  legible en un tono oscuro — reemplaza temporalmente el relleno púrpura/gris del chip mientras
 *  el cambio no se guarda. El `!` fuerza la utilidad por sobre el color de `.tag-item--active/--inactive`
 *  (mismo patrón que `!bg-[#6D57BC]` ya usado en el botón Guardar Cambios de este archivo).
 *  Verde = alta pendiente, rojo = baja pendiente. */
function pendingIndicatorClass(name: string, pending: PendingChangeSet): string {
  const key = normalizeForCompare(name);
  if (pending.added.has(key)) return "ring-2 ring-green-500 ring-offset-1 !bg-green-50 !text-green-800";
  if (pending.removed.has(key)) return "ring-2 ring-red-500 ring-offset-1 !bg-red-50 !text-red-800";
  return "";
}

export const ProfileAttentionTab: React.FC<ProfileAttentionTabProps> = ({
  professionalData,
  toggleTag,
  servicesLoading,
  loadingProfile,
  preferencesCatalogLoading,
  otherPreferenceGroups,
  toggleOtherPreference,
  pendingOtherChanges,
  dedicatedSectionLabels,
  onSave,
  canSave,
  pendingAttentionChanges,
  patientLimit,
  hidePatientLimit,
  onGuideReplay,
}) => {
  // App mobile: sin onboarding guiado (solo web).
  const isPwaApp = useIsPwaApp();
  return (
    <div id="profile-attention-section">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-4 sm:mb-6">
        <div className="flex items-start gap-[0.4rem]">
          <h4 className="section-title !mb-0">Sobre tu atención</h4>
          <InfoTooltip
            ariaLabel="Información sobre tu atención"
            className="mt-1"
            text="Aquí verás los servicios, temáticas y enfoques que puedes ofrecer. Selecciona solo aquello en lo que te consideres con experiencia. Haz click para activar o desactivar cada opción y recuerda presionar 'Guardar Cambios' para aplicar las actualizaciones. Es importante mantener estas preferencias actualizadas, ya que recibirás pacientes de acuerdo con lo indicado."
          />
        </div>
        {onGuideReplay && !isPwaApp && (
          <button
            type="button"
            onClick={onGuideReplay}
            title="Repasar guía de Sobre tu atención"
            aria-label="replay-attention-onboarding"
            className="flex-shrink-0 inline-flex items-center gap-1 text-[0.75rem] font-normal text-[#8475b3] hover:text-[#6D57BC] bg-transparent border-0 cursor-pointer transition-colors"
          >
            <Info size={14} />
            Repasar guía de Sobre tu atención
          </button>
        )}
      </div>


      {/* Cantidad de pacientes */}
      {!hidePatientLimit && patientLimit.processLimit != null && patientLimit.draft != null && (
        <PatientLimitField
          value={patientLimit.draft}
          processLimit={patientLimit.processLimit}
          currentAssignedProcesses={patientLimit.currentAssignedProcesses}
          onChange={patientLimit.onChange}
          onApplied={patientLimit.onApplied}
          isModified={patientLimit.isModified}
          psychologistId={patientLimit.psychologistId}
          internalLimit={patientLimit.processLimit}
        />
      )}

      {/* Servicios */}
      <div id="attention-tour-services" className="tags-container">
        <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
          <p className="text-sm sm:text-base font-medium text-gray-700 m-0">Servicios</p>
          <InfoTooltip
            ariaLabel="Información sobre servicios"
            text="Los servicios definen el tipo de atención que brindas (por ejemplo, Individual, Pareja, Familia). Selecciona únicamente los que realmente ofreces. Algunas opciones pueden requerir certificación antes de hacerse visibles en la plataforma."
          />
        </div>
        <div className="tags-list">
          {servicesLoading || loadingProfile ? (
            <div className="w-full flex justify-center p-4">
              <CircularProgress sizeRem={2} color="#6D57BC" label="Cargando servicios..." />
            </div>
          ) : (
            professionalData.services.map((service, index) => (
              // Ocultar Supervisión, Material Digital y Talleres/charlas (se devuelve null
              // en vez de filtrar para no desalinear el index que usa toggleTag)
              ["Supervisión", "Material Digital", "Talleres/charlas"].includes(service.name) ? null : (
              <button
                key={index}
                className={`tag-item ${
                  // validating/needs_additional_document priman sobre "active": el toggle ya
                  // está prendido localmente, pero todavía no está confirmado — no mostrar
                  // el chip como definitivamente activo hasta que la IA resuelva.
                  service.certStatus === "validating"
                    ? "tag-item--validating"
                    : service.certStatus === "needs_additional_document"
                      ? "tag-item--needs-doc"
                      : service.active
                        ? "tag-item--active"
                        : service.certStatus === "rejected_escalated"
                          ? "tag-item--in-review"
                          : "tag-item--inactive"
                } ${
                  service.certStatus
                    ? ""
                    : pendingIndicatorClass(service.name, pendingAttentionChanges.services)
                }`}
                onClick={(e) => toggleTag("services", index, e)}
                title={
                  service.certStatus === "validating"
                    ? `Estamos validando la documentación para ${service.name}.`
                    : service.certStatus === "needs_additional_document"
                      ? `Necesitamos documentación adicional para ${service.name}.`
                      : service.certStatus === "rejected_escalated"
                        ? `La documentación para ${service.name} no pudo validarse automáticamente. Hacé click para subir nueva.`
                        : undefined
                }
              >
                {service.name}
                {service.certStatus === "validating" && <span className="ml-1 text-xs">⏳</span>}
                {service.certStatus === "needs_additional_document" && <span className="ml-1 text-xs">📄</span>}
                {service.certStatus === "rejected_escalated" && <span className="ml-1 text-xs">⚠️</span>}
              </button>
              )
            ))
          )}
        </div>
      </div>

      {/* Temáticas */}
      <div id="attention-tour-themes" className="tags-container">
        <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
          <p className="text-sm sm:text-base font-medium text-gray-700 m-0">{dedicatedSectionLabels.themes}</p>
          <InfoTooltip
            ariaLabel="Información sobre temáticas"
            text="Las temáticas representan áreas problemáticas o intereses de trabajo clínico (ansiedad, duelo, trauma, etc.). Indica aquellas en las que tienes formación y experiencia. Recuerda que 'Desarrollo personal' es obligatoria. Puedes guardar tus preferencias con solo esa temática activa."
          />
        </div>
        <div className="tags-list">
          {preferencesCatalogLoading ? (
            <div className="w-full flex justify-center p-4">
              <CircularProgress sizeRem={2} color="#6D57BC" label="Cargando temáticas..." />
            </div>
          ) : (
            professionalData.themes.map((theme, index) => (
              <button
                key={index}
                className={`tag-item ${theme.active ? "tag-item--active" : "tag-item--inactive"} ${pendingIndicatorClass(theme.name, pendingAttentionChanges.themes)}`}
                onClick={(e) => toggleTag("themes", index, e)}
              >
                {theme.name}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Enfoques */}
      <div id="attention-tour-approaches" className="tags-container">
        <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
          <p className="text-sm sm:text-base font-medium text-gray-700 m-0">{dedicatedSectionLabels.approaches}</p>
          <InfoTooltip
            ariaLabel="Información sobre enfoques"
            text="Los enfoques corresponden a marcos teóricos o técnicas predominantes (TCC, Gestalt, EMDR, etc.). Selecciona al menos uno que describa tu práctica habitual. Puedes combinar varios, priorizando los que usas con mayor frecuencia."
          />
        </div>
        <div className="tags-list">
          {preferencesCatalogLoading ? (
            <div className="w-full flex justify-center p-4">
              <CircularProgress sizeRem={2} color="#6D57BC" label="Cargando enfoques..." />
            </div>
          ) : (
            professionalData.approaches.map((approach, index) => (
              <button
                key={index}
                className={`tag-item ${approach.active ? "tag-item--active" : "tag-item--inactive"} ${pendingIndicatorClass(approach.name, pendingAttentionChanges.approaches)}`}
                onClick={(e) => toggleTag("approaches", index, e)}
              >
                {approach.name}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Filtros custom habilitados para psi-platform además de Temáticas/Enfoques —
          clickeables si `writable` (api-core ya sabe persistir ese type); si no,
          se muestran de solo lectura para que la psicóloga sepa qué tiene
          configurado sin poder tocarlo por accidente (ver PsychologistProfile.tsx). */}
      {otherPreferenceGroups.map((group) => (
        <div key={group.filterKey} className="tags-container">
          <div className="flex items-center gap-[0.4rem] mb-3 sm:mb-4">
            <p className="text-sm sm:text-base font-medium text-gray-700 m-0">{group.label}</p>
            <InfoTooltip
              ariaLabel={`Información sobre ${group.label}`}
              text={group.writable
                ? `Selecciona las opciones de "${group.label}" en las que te consideres con experiencia.`
                : "Esta configuración la administra el equipo de Psi Mammoliti — todavía no se puede editar desde acá."}
            />
          </div>
          <div className="tags-list">
            {group.items.map((item, index) => group.writable ? (
              <button
                key={index}
                className={`tag-item ${item.active ? "tag-item--active" : "tag-item--inactive"} ${pendingIndicatorClass(item.name, pendingOtherChanges[group.filterKey] ?? { added: new Set(), removed: new Set() })}`}
                onClick={(e) => toggleOtherPreference(group.filterKey, index, e)}
              >
                {item.name}
              </button>
            ) : (
              <span
                key={index}
                className={`tag-item ${item.active ? "tag-item--active" : "tag-item--inactive"} !cursor-default`}
                title="Esta configuración la administra el equipo de Psi Mammoliti — todavía no se puede editar desde acá."
              >
                {item.name}
              </span>
            ))}
          </div>
        </div>
      ))}

      <button
        type="button"
        id="attention-tour-save"
        onClick={onSave}
        disabled={!canSave}
        className={`save-changes-button ${canSave ? '!bg-[#6D57BC] !cursor-pointer' : ''}`}
      >
        Guardar Cambios
      </button>
    </div>
  );
};

export default ProfileAttentionTab;
